<template>
  <div class="view-wrapper channels-view">
    <ViewHeader :title="$store.getters.currentPlan.name">
      <template #actions>
        <Button label="Add Tactic" icon="pi pi-plus-circle" class="p-mr-2" @click="onAddTactic" />
        <Button label="Filters" icon="pi pi-sliders-h" class="p-button-outlined" @click="showTacticFiltersView = true"
          :badge="$store.getters.activeFiltersCount" badgeClass="p-badge-info" />
        <template v-if="isLeadPlan || isNestedPlan">
          <LeadNestedFilters />
        </template>
      </template>
      <template>
        <div class="p-d-flex p-ai-end">
          <DateRangePicker :isRangeView="true" :isDisabled="shouldShowUnscheduled" />

          <div class="parking-lot-switch-container p-field p-d-flex p-ml-4 p-mb-2 p-ai-center">
            <label for="shouldShowUnscheduled" class="p-mr-2">Scheduled Tactics</label>
            <InputSwitch id="shouldShowUnscheduled" v-model="shouldShowUnscheduled" />
            <label for="shouldShowUnscheduled" class="p-ml-2">Parking Lot</label>
            <span
              v-tooltip.bottom="'The Parking Lot holds unscheduled Tactic ideas for the future. Simply select &quot;Parking Lot&quot; when creating or editing a Tactic. They will be saved under the Table view, Parking Lot. Turn off the designation when you are ready to schedule.'"
              class="pi pi-question-circle tooltip-color p-ml-2 p-mb-2"></span>
          </div>

          <div v-if="!shouldShowUnscheduled" class="p-ml-auto p-d-flex p-ai-center p-jc-end">
            <!-- BEGIN: Archiving TacticImport.vue - JV 04.18.23 -->
            <!-- <Button
              v-if="selectedType.id"
              label="Import Tactics"
              icon="pi pi-upload"
              class="import-export-tactics-button p-mr-3"
              @click="onClickImportTactics()"
            /> -->
            <!-- END: Archiving TacticImport.vue - JV 04.18.23 -->
              
            <p class="export-message p-mr-3">Channel, Initiative, and Tag Filters are not applied to CSV exports.</p>
            <Button label="CSV Export" icon="pi pi-download" class="import-export-tactics-button" @click="onClickExportTactics()" />
          </div>
        </div>
      </template>
    </ViewHeader>
    <ViewMain>
      <DataTable v-if="currentTactics.length" :value="sortedTactics"
        :key="`${dateRange[0].getTime()}-${dateRange[1].getTime()}-${filteredInitiativesInCategories.map(t => t.id.intID).join('-')}`" :autoLayout="true"
        @sort="onSort">

        <template v-if="shouldShowUnscheduled">
          <!-- Parking Lot columns -->
          <Column field="title" header="Topic" headerClass="title-column" bodyClass="title-column" sortable
            key="parking_lot_title">
            <template #body="slotProps">
              <span :class="{
                'is-nested': slotProps.data.isNested,
                'is-lead': slotProps.data.isLead,
              }">
                {{ getTitleForTactic(slotProps.data) }}
              </span>
            </template>
          </Column>
          <Column field="channelName" header="Channel" sortable key="parking_lot_channel">
            <template #body="slotProps">
              {{ slotProps.data.channelName }}
            </template>
          </Column>
          <Column field="tacticTypeName" header="Tactic Type" sortable key="parking_lot_type">
            <template #body="slotProps">
              {{ slotProps.data.tacticTypeName }}
            </template>
          </Column>
          <template v-for="(tagCategory) in tagsInCategories">
            <Column :key="'parking_lot_' + tagCategory.name + tagCategory.id"
              :columnKey="tagCategory.name + tagCategory.id" :header="tagCategory.name" v-if="tagCategory.tags.length"
              :sortField="'tc_' + tagCategory.id" sortable>
              <template #body="slotProps">
                <template v-if="getTagsForTagCategoryAndTactic(tagCategory, slotProps.data).length">
                  <template v-for="(tag, index) in getTagsForTagCategoryAndTactic(tagCategory, slotProps.data)">
                    <span class="tag-title" :key="index">
                      {{ tag.title.trim() }}
                      <template v-if="index + 1 < getTagsForTagCategoryAndTactic(tagCategory, slotProps.data).length">
                        &nbsp;/&nbsp;
                      </template>
                    </span>
                  </template>
                </template>
              </template>
            </Column>
          </template>
          <Column field="summary" header="Notes" key="parking_lot_summary">
            <template #body="slotProps">
              <Button v-if="hasTacticSummary(slotProps.data)" icon="pi pi-eye"
                class="p-button-icon-only p-button-rounded p-button-text"
                @mouseenter="(event) => showSummaryOverlay(event, slotProps.data)"
                @mouseleave="hideSummaryOverlay()"></Button>
            </template>
          </Column>
          <template v-if="$store.getters.currentPlan.isBudgetEnabled">
            <Column field="estimatedCost" header="Estimated Cost" key="parking_lot_estimated_cost"></Column>
            <Column field="actualCost" header="Actual Cost" key="parking_lot_actual_cost"></Column>
          </template>
          <Column key="parking_lot_open_button">
            <template #body="slotProps">
              <div class="p-d-flex p-jc-end">
                <Button label="Open" icon="pi pi-arrow-circle-up" class="p-button-text"
                  @click="showTactic(slotProps.data)"></Button>
              </div>
            </template>
          </Column>
          <!-- END Parking Lot Columns -->
        </template>
        <template v-else>
          <!-- Scheduled Tactics Columns -->
          <Column field="title" header="Topic" headerClass="title-column" bodyClass="title-column" sortable
            key="scheduled_title">
            <template #body="slotProps">
              <span :class="{
                'is-nested': slotProps.data.isNested,
                'is-lead': slotProps.data.isLead,
              }">
                {{ getTitleForTactic(slotProps.data) }}
              </span>
            </template>
          </Column>
          <Column field="channelName" header="Channel" sortable key="scheduled_channel">
            <template #body="slotProps"> 
              {{ slotProps.data.channelName }}
            </template>
          </Column>
          <Column field="tacticTypeName" header="Tactic Type" sortable key="scheduled_type">
            <template #body="slotProps">
              {{ slotProps.data.tacticTypeName }}
            </template>
          </Column>
          <Column sortField="tactic_platforms" header="Platforms" sortable key="scheduled_platforms">
            <template #body="slotProps">
              {{slotProps.data.tacticPlatforms.map((platform) => platform.name).join(', ')}}
            </template>
          </Column>
          <Column field="startDate" header="Start Date" sortable key="scheduled_start">
            <template #body="slotProps">
              {{ slotProps.data.startDate.toLocaleDateString() }}
            </template>
          </Column>
          <Column field="endDate" header="End Date" sortable key="scheduled_end">
            <template #body="slotProps">
              {{ slotProps.data.endDate.toLocaleDateString() }}
            </template>
          </Column>
          <Column header="Notes" field="summary" key="scheduled_summary">
            <template #body="slotProps">
              <Button v-if="hasTacticSummary(slotProps.data)" icon="pi pi-eye"
                class="p-button-icon-only p-button-rounded p-button-text"
                @mouseenter="(event) => showSummaryOverlay(event, slotProps.data)"
                @mouseleave="hideSummaryOverlay()"></Button>
            </template>
          </Column>
          <template v-for="(customField, index) in universalCustomFieldsWithData">
            <Column :key="customField.name + customField.id + index + '_repeats'"
              :columnKey="customField.name + customField.id" :header="customField.name"
              :sortField="'cf_' + customField.id.intID" sortable>
              <template #body="slotProps">
                <button v-if="
                  customField.type == 'image' &&
                  getCustomImageFieldUrl(slotProps.data.id, customField.id)
                " class="custom-field-image-budgets-view" @click="
                  () => {
                    showCustomFieldImageDialog = true
                    customFieldDialogImage = getCustomImageFieldUrl(
                      slotProps.data.id,
                      customField.id
                    )
                  }
                ">
                  <img :src="getCustomImageFieldUrl(slotProps.data.id, customField.id)" alt="custom image" />
                </button>
                <span v-else-if="customField.type === 'url'">
                  <a :href="getFieldValueForTactic(customField.id, slotProps.data)" target="_blank">{{
                    getFieldValueForTactic(customField.id, slotProps.data) }}</a>
                </span>
                <span v-else-if="customField.type !== 'image'">
                  {{ getFieldValueForTactic(customField.id, slotProps.data) }}
                </span>
              </template>
            </Column>
          </template>
          <template v-for="(tagCategory) in tagsInCategories">
            <Column :key="tagCategory.name + tagCategory.id" :columnKey="tagCategory.name + tagCategory.id"
              :header="tagCategory.name" v-if="tagCategory.tags.length"
              :sortField="'tc_' + tagCategory.id" sortable>
              <template #body="slotProps">
                <template v-if="getTagsForTagCategoryAndTactic(tagCategory, slotProps.data).length">
                  <template v-for="(tag, index) in getTagsForTagCategoryAndTactic(tagCategory, slotProps.data)">
                    <span class="tag-title" :key="index">{{ tag.title.trim() }}<template v-if="index + 1 < getTagsForTagCategoryAndTactic(tagCategory, slotProps.data).length">,&nbsp;</template></span>
                  </template>
                </template>
              </template>
            </Column>
          </template>
          <template v-for="(type) in filteredInitiativesInCategories">
            <Column :key="'initiative-type-' + type.id.intID" :columnKey="'initiative-type-' + type.id.intID" :header="type.name"
              :sortField="'initiative-type-' + type.id.intID" sortable>
              <template #body="slotProps">
                <span v-if="getInitiativeForMapping(getInitiativeMappingForTypeAndTactic(type, slotProps.data))">
                  <i v-if="getInitiativeForMapping(getInitiativeMappingForTypeAndTactic(type, slotProps.data)).isInitiativeCustomization"
                    class="pi pi-link" style="margin-right: 0.5rem;">
                  </i>
                  <i v-else-if="getInitiativeForMapping(getInitiativeMappingForTypeAndTactic(type, slotProps.data)).isLead"
                    class="pi pi-home" style="margin-right: 0.5rem;">
                  </i>
                  <span
                    v-if="getInitiativeForMapping(getInitiativeMappingForTypeAndTactic(type, slotProps.data)).abbreviatedPlanName">
                    {{ getInitiativeForMapping(getInitiativeMappingForTypeAndTactic(type,
                      slotProps.data)).abbreviatedPlanName }} &vert;
                  </span>
                  {{ getInitiativeMappingForTypeAndTactic(type, slotProps.data).initiativeTitle.trim() }}
                </span>
              </template>
            </Column>
          </template>
          <template v-if="$store.getters.currentPlan.isBudgetEnabled">
            <Column field="estimatedCost" header="Estimated Cost" key="scheduled_estimated_cost"></Column>
            <Column field="actualCost" header="Actual Cost" key="scheduled_actual_cost"></Column>
          </template>
          <Column key="scheduled_open_button">
            <template #body="slotProps">
              <div class="p-d-flex p-jc-end">
                <Button label="Open" icon="pi pi-arrow-circle-up" class="p-button-text"
                  @click="showTactic(slotProps.data)"></Button>
              </div>
            </template>
          </Column>
          <!-- END Scheduled Tactics Columns -->
        </template>


        <!-- Media section hidden for MVP -->
        <!-- <Column field="impressions" header="Impressions"></Column>
        <Column field="ratingPoints" header="Rating Points"></Column> -->

        <!-- Hiding columns for custom fields. - JV 05.29.24 -->
        <!-- <template v-for="field in currentTypeFields">
          <Column :key="field.key" :header="field.name">
            <template #body="slotProps">
              <button
                v-if="
                  field.type == 'image' &&
                  getFieldValueForTactic(field.id, slotProps.data)
                "
                class="custom-field-image-budgets-view"
                @click="
                  () => {
                    showCustomFieldImageDialog = true
                    customFieldDialogImage = getCustomImageFieldUrl(
                      slotProps.data.id,
                      field.id
                    )
                  }
                "
              >
                <img
                  :src="getCustomImageFieldUrl(slotProps.data.id, field.id)"
                  alt=""
                />
              </button>
              <span v-else-if="field.type === 'url'">
                <a
                  :href="getFieldValueForTactic(field.id, slotProps.data)"
                  target="_blank"
                  >{{ getFieldValueForTactic(field.id, slotProps.data) }}</a
                >
              </span>
              <span v-else-if="field.type === 'richtext'">
                <Button
                    icon="pi pi-eye"
                    class="p-button-icon-only p-button-rounded p-button-text"
                    @mouseenter="(event)=> {
                      currentOverlayHTML = getFieldValueForTactic(field.id, slotProps.data)
                      $refs.richTextFieldOverlay.show(event)
                    }"
                    @mouseleave="(event)=> {
                      $refs.richTextFieldOverlay.hide()
                    }"
                  ></Button>
              </span>
              <span v-else>
                {{ getFieldValueForTactic(field.id, slotProps.data) }}
              </span>
            </template>
          </Column>
        </template> -->

      </DataTable>

      <Dialog :dismissableMask="true" header="" :visible.sync="showCustomFieldImageDialog" :modal="true"
        @hide="() => (customFieldDialogImage = '')">
        <div class="custom-image-dialog">
          <Button label="Download" class="p-button-lg p-button-outlined" icon="pi pi-download"
            @click="openInNewWindow(customFieldDialogImage)" />
          <img :src="customFieldDialogImage" alt="" />
        </div>
      </Dialog>

      <TacticFilters :showHide="showTacticFiltersView" @shouldHide="showTacticFiltersView = false" />

    </ViewMain>
    <div class="copyright-container">
      <p class="copyright">Copyright &copy; {{ currentYear }} Annum. All rights reserved. United States Patent Number 12,079,779.</p>
    </div>

    <OverlayPanel ref="richTextFieldOverlay" class="rich-text-field-overlay" appendTo="body">
      <div class="notes-field-content-container" v-html="currentOverlayHTML"></div>
    </OverlayPanel>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import ViewHeader from '@/components/ViewHeader.vue'
import ViewMain from '@/components/ViewMain.vue'
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import Dropdown from 'primevue/dropdown'
import ID from '@/models/ID'
import DateRangePicker from '@/components/DateRangePicker.vue'
import Channel from '@/models/Channel'
import TacticType from '@/models/TacticType'
import TacticPlatform from '@/models/TacticPlatform'
import Tactic from '@/models/Tactic'
import TacticField from '@/models/TacticField'
import TacticFieldValue from '@/models/TacticFieldValue'
import Tag from '@/models/Tag'
import { AlertMessageSeverity } from '@/models/AlertMessage'
import Role from '@/models/Role'
import { mapState } from 'vuex';
import { format, isSameDay, isThisISOWeek } from 'date-fns'
import { stripExDates } from '@/utils/recurrence'
import RelatedInitiativeMapping from '@/models/RelatedInitiativeMapping'
import TagCategory from '@/models/TagCategory'
import InputSwitch from 'primevue/inputswitch'
import TacticFilters from '@/components/TacticFilters.vue'
import Tooltip from 'primevue/tooltip'
import Plan from '@/models/Plan'
import Initiative from '@/models/Initiative'
import InitiativeCustomization from '@/models/InitiativeCustomization'
import InitiativeType from '@/models/InitiativeType'
import TacticDisplayMixin from '@/mixins/TacticDisplayMixin'
import LeadNestedFilters from '@/components/LeadNestedFilters.vue'
import MediaAsset from '@/models/MediaAsset'

Vue.component('Button', Button)
Vue.component('Column', Column)
Vue.component('DataTable', DataTable)
Vue.component('Dropdown', Dropdown)

export default Vue.extend({
  name: 'Table',
  mixins: [TacticDisplayMixin],

  components: {
    ViewHeader,
    ViewMain,
    DateRangePicker,
    TacticFilters,
    LeadNestedFilters,
  },
  directives: {
    tooltip: Tooltip,
  },
  data: () => {
    return {
      showCustomFieldImageDialog: false as boolean,
      customFieldDialogImage: '' as string,
      customImageFieldUrlsMap: [] as {
        tacticId: number | string;
        fieldId: number | string;
        url: string;
      }[],
      currentOverlayHTML: '' as string,
      shouldShowUnscheduled: false as boolean,
      showTacticFiltersView: false as boolean,
      sortedTactics: [] as Tactic[],
      currentSortField: '' as string,
      currentSortOrder: 1 as number,
      tacticsUpdateIterator: 0 as number,
      tacticsUpdateDebounceTimer: null as ReturnType<typeof setTimeout> | null,
    }
  },
  computed: {
    currentYear(): number {
      return new Date().getFullYear()
    },
    currentTypes(): TacticType[] {
      return this.$store.getters.selectedTacticTypes
    },
    currentPlan(): Plan {
      return this.$store.getters.currentPlan
    },
    isCsvExportDisabled(): boolean {
      return (this.currentPlan.parentId.intID !== 0 && this.$store.getters.isLeadPlanVisible) || (this.currentPlan.plans.length > 0 && this.$store.getters.visibleNestedPlansIntIds.length > 0)
    },
    // Hiding columns for custom fields. - JV 05.29.24
    // currentTypeFields(): TacticField[] {
    //   let returnTacticFields = [] as TacticField[]
    //   this.currentTypes.forEach((type)=>{
    //     returnTacticFields = returnTacticFields.concat(type.tacticFields.map((field)=>field))
    //   })
    //   this.currentPlan.tacticFields.forEach((field)=>{
    //     returnTacticFields = returnTacticFields.concat(field)
    //   })
    //   return returnTacticFields
    // },
    currentTactics(): Tactic[] {
      // Access tacticsUpdateIterator to create a reactive dependency for forced re-computation
      this.tacticsUpdateIterator
      const returnTactics = [] as Tactic[]
      this.currentTypes.forEach((type) => {
        // Get tactics along with clones for recurring tactics
        type.getTacticsWithClonesForDateRange(this.dateRange).forEach((tactic) => {
          if (
            !(this as any).isTacticInFilteredOutPlan(tactic) &&
            !(this as any).isTacticRelatedToDeselectedTag(tactic) &&
            !(this as any).isTacticRelatedToDeselectedInitiative(tactic) &&
            !this.isTacticMonthlyFocusInDisabledChannel(tactic, type) &&
            (
              (this.shouldShowUnscheduled && tactic.isUnscheduled) ||
              (
                (!this.shouldShowUnscheduled && !tactic.isUnscheduled) &&
                (tactic.startDate <= this.dateRange[1] && tactic.endDate >= this.dateRange[0])
              )
            )
          ) {
            returnTactics.push(tactic)
          }
        })
      })
      return returnTactics
    },
    dateRange(): Date[] {
      const adjustedDateRangeStart = new Date(
        this.$store.getters.currentDateRange[0]
      )
      adjustedDateRangeStart.setHours(0, 0, 0, 0)
      const adjustedDateRangeEnd = new Date(
        this.$store.getters.currentDateRange[1]
      )
      adjustedDateRangeEnd.setHours(23, 59, 59, 999)
      return [adjustedDateRangeStart, adjustedDateRangeEnd]
    },
    canUserAddTactics(): boolean {
      return this.$store.getters.getPermissionLevelForPlanId(this.$store.getters.currentPlan.id) <=
        Role.LEVEL_CONTRIBUTOR
        ? true
        : false
    },
    canUserExportTactics(): boolean {
      return (
        this.$store.getters.currentAccountPermissionLevel <=
        Role.LEVEL_CONTRIBUTOR
      )
    },
    isLeadPlan(): boolean {
      return this.$store.getters.currentPlan.plans.length ? true : false
    },
    isNestedPlan(): boolean {
      return this.$store.getters.currentPlan.parentId.intID !== 0 ? true : false
    },
    filteredInitiativesInCategories(): InitiativeType[] {
      const returnArray = [] as InitiativeType[]
      this.$store.getters.allInitiativesInCategories.forEach((type) => {
        const thisType = { ...type }
        thisType.initiatives = type.initiatives.filter((initiative) => (!this.isLeadPlan || (this.isLeadPlan && !initiative.isNested)) && (this.currentTactics.filter((tactic) => tactic.relatedInitiatives.filter((initiativeMapping) => initiativeMapping.initiativeId.intID === initiative.id.intID || initiative.initiativeCustomizations?.some((customization: InitiativeCustomization) => customization.id.intID === initiativeMapping.initiativeId.intID)).length > 0).length > 0))
        if (thisType.initiatives.length > 0) {
          returnArray.push(thisType)
        }
      })
      return returnArray.sort((a, b) => a.orderIndex - b.orderIndex)
    },
    tagsInCategories(): TagCategory[] {
      return this.$store.getters.currentPlanTagCategories
    },
    shouldShowRepeatColumns(): boolean {
      return this.currentTactics.filter((tactic) => tactic.rrule !== '' && tactic.rrule !== null).length > 0 ? true : false
    },
    universalCustomFieldsWithData(): TacticField[] {
      const currentPlan: Plan = this.$store.getters.currentPlan
      let planFields: TacticField[] = currentPlan.tacticFields
      if (currentPlan.isNested) {
        const parentPlan = this.$store.getters.allPlans.find(
          (plan: Plan) => plan.id.intID === currentPlan.parentId.intID
        )
        if (parentPlan) {
          planFields = parentPlan.tacticFields.concat(currentPlan.tacticFields)
        }
      }
      return planFields.filter((field) =>
        this.currentTactics.some((tactic) =>
          tactic.typeValues.some((value) => value.tacticFieldId.intID === field.id.intID)
        )
      )
    },
  },
  beforeDestroy() {
    if (this.tacticsUpdateDebounceTimer) {
      clearTimeout(this.tacticsUpdateDebounceTimer)
    }
  },
  mounted: function () {
    // Set date range to non-month range
    this.$store.dispatch('updateCurrentDateRange', { newDateRange: this.$store.getters.rangeViewsDateRange })

    this.mapImageFieldUrls()
  },
  watch: {
    shouldShowUnscheduled(newValue: boolean) {
      if (newValue) {
        this.$store.dispatch('fetchUnscheduledTacticsForCurrentPlan')
      }
    },
    '$store.getters.currentDateRange': {
      deep: true,
      handler() {
        // Debounce re-computation to avoid multiple updates during tactics fetch
        if (this.tacticsUpdateDebounceTimer) {
          clearTimeout(this.tacticsUpdateDebounceTimer)
        }
        this.tacticsUpdateDebounceTimer = setTimeout(() => {
          this.tacticsUpdateIterator += 1
          this.tacticsUpdateDebounceTimer = null
        }, 150)
      },
    },
    currentTactics: {
      immediate: true,
      handler(newVal: Tactic[]) {
        this.sortedTactics = [...newVal]
        if (this.currentSortField) {
          this.applySort(this.currentSortField, this.currentSortOrder)
        }
        this.mapImageFieldUrls()
      }
    },
  },
  methods: {
    onSort(event: { sortField: string | null | undefined; sortOrder: number }) {
      const field = event.sortField
      if (field) {
        this.currentSortField = field
        this.currentSortOrder = event.sortOrder
        this.applySort(field, event.sortOrder)
      } else {
        this.currentSortField = ''
        this.sortedTactics = [...this.currentTactics]
      }
    },
    applySort(field: string, sortOrder: number) {
      this.sortedTactics = [...this.currentTactics].sort((a, b) => {
        const valueA = this.getSortValueForTactic(field, a)
        const valueB = this.getSortValueForTactic(field, b)
        if (valueA === valueB) return 0
        if (valueA === '\uffff') return 1
        if (valueB === '\uffff') return -1
        const comparison = typeof valueA === 'string' && typeof valueB === 'string'
          ? valueA.localeCompare(valueB)
          : (valueA < valueB ? -1 : 1)
        return comparison * sortOrder
      })
    },
    getSortValueForTactic(field: string, tactic: Tactic): string | number | Date {
      // Custom fields
      if (field.startsWith('cf_')) {
        const fieldIntId = parseInt(field.slice(3))
        const customField = this.universalCustomFieldsWithData.find((f) => f.id.intID === fieldIntId)
        return customField ? (this.getFieldValueForTactic(customField.id, tactic) || '\uffff') : '\uffff'
      }
      // Tag categories
      if (field.startsWith('tc_')) {
        const catId = parseInt(field.slice(3))
        const tagCategory = this.tagsInCategories.find((cat) => cat.id === catId)
        if (!tagCategory) return '\uffff'
        const tags = this.getTagsForTagCategoryAndTactic(tagCategory, tactic)
          .map((t) => t.title.trim())
          .join(', ')
        return tags || '\uffff'
      }
      // Initiative types (field = 'initiative-type-{intID}')
      if (field.startsWith('initiative-type-')) {
        const typeIntId = parseInt(field.replace('initiative-type-', ''), 10)
        const mapping = tactic.relatedInitiatives.find((m) => m.initiativeTypeId.intID === typeIntId)
        return mapping ? mapping.initiativeTitle.trim() || '\uffff' : '\uffff'
      }
      // Platforms — sort by first platform name; empty lists sort last
      if (field === 'tactic_platforms') {
        return tactic.tacticPlatforms.length > 0
          ? tactic.tacticPlatforms[0].name
          : '\uffff'
      }
      // Standard tactic fields
      return (tactic as unknown as Record<string, string | number | Date>)[field] ?? ''
    },
    getInitiativeMappingForTypeAndTactic(type: TacticType | InitiativeType, tactic: Tactic): RelatedInitiativeMapping {
      const thisMappingObj = tactic.relatedInitiatives.find((mappingObj: RelatedInitiativeMapping) => mappingObj.initiativeTypeId.intID === type.id.intID)
      return thisMappingObj ? thisMappingObj : new RelatedInitiativeMapping()
    },
    getInitiativeForMapping(mapping: RelatedInitiativeMapping) {
      if (!mapping || !mapping.initiativeId || mapping.initiativeId.intID === 0) {
        return null
      }

      // Find the tactic to get its planId
      const tactic = this.currentTactics.find((t) => t.id.intID === mapping.tacticId.intID)
      if (!tactic) {
        return null
      }

      // First, try to find a top-level initiative the mapping points to
      const initiative = this.$store.getters.currentInitiatives.find(
        (i) => i.id.intID === mapping.initiativeId.intID
      )

      if (initiative) {
        // If a customization exists for this tactic's plan, prefer it
        if (initiative.initiativeCustomizations && initiative.initiativeCustomizations.length > 0) {
          const customization = initiative.initiativeCustomizations.find(
            (c) => c.planId.intID === tactic.planId.intID
          )
          if (customization) {
            // Return the customization with a flag to show the link icon
            return customization
          }
        }

        // Return the initiative itself
        return initiative
      }

      // The mapping may point directly to a customization (e.g. a nested plan's
      // own tactic connected to that plan's variant of a lead initiative). In
      // that case mapping.initiativeId is the customization's id, which is not a
      // top-level initiative, so resolve it from the parent initiatives' customizations.
      for (const parentInitiative of this.$store.getters.currentInitiatives as Initiative[]) {
        const customization = parentInitiative.initiativeCustomizations?.find(
          (c: InitiativeCustomization) => c.id.intID === mapping.initiativeId.intID
        )
        if (customization) {
          return customization
        }
      }

      return null
    },
    getTagsForTagCategoryAndTactic(tagCategory: TagCategory, tactic: Tactic): Tag[] {
      return tagCategory.tags
        .filter((tag) => tactic.tagIds.map((id) => id.intID).includes(tag.id.intID))
        .sort((a, b) => a.title.trim().localeCompare(b.title.trim()))
    },

    isTacticMonthlyFocusInDisabledChannel(tactic: Tactic, type: TacticType) {
      if (type.name.toLowerCase() !== this.$store.getters.monthlyFocusTypeName) {
        return false
      }

      const thisChannel = this.$store.getters.currentPlan.channels.find((channel) => channel.id.intID === type.channelId.intID)
      if (thisChannel && thisChannel.useMonthlyFocusType == false) {
        return true
      }

      return false
    },

    // onClickImportTactics() {
    //   this.$router.push({
    //     path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$store.getters.currentPlan.id.intID}/import`
    //   })
    // },

    formatDateForExport(date) {
      const d = new Date(date)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    onClickExportTactics() {
      const visibleNestedIds = this.$store.getters.visibleNestedPlansIntIds as number[]
      const planIds: number[] = []
      if (this.currentPlan.isNested) {
        planIds.push(this.currentPlan.id.intID)
        if (this.$store.getters.isLeadPlanVisible) {
          planIds.push(this.currentPlan.parentId.intID)
        }
      } else {
        if (!this.currentPlan.isLead || this.$store.getters.isLeadPlanVisible) {
          planIds.push(this.currentPlan.id.intID)
        }
        planIds.push(...this.currentPlan.plans.filter((p) => visibleNestedIds.includes(p.intID)).map((p) => p.intID))
      }
      this.$store.getters.services.plans
        .exportPlans(planIds, this.formatDateForExport(this.dateRange[0]), this.formatDateForExport(this.dateRange[1]))
        .then(
          (response) => {
            window.open(response.url, '_blank')
          },
          (error) => {
            Vue.prototype.$toast.add({
              severity: AlertMessageSeverity.error,
              summary: 'Error exporting plan tactics.',
              detail: error,
            })
          }
        )
    },
    openInNewWindow(url) {
      if (url.substr(0, 10).indexOf('://') == -1) {
        url = `https://${url}`
      }
      window.open(url, '_blank')
    },
    getNameForInitiativeID(initiativeID: ID): string {
      let returnString = ''
      this.$store.getters.currentInitiatives.filter((tactic) => {
        if (tactic.id.intID == initiativeID.intID) {
          returnString = tactic.title
        }
      })
      return returnString
    },
    getFieldValueForTactic(fieldId: ID, tactic: Tactic): string {
      let returnString = ''
      if (tactic.typeValues.length) {
        const fieldValue = tactic.typeValues.find(
          (value) => value.tacticFieldId.intID == fieldId.intID
        )
        if (fieldValue) {
          returnString = fieldValue.value.toString()
        }
      }
      return returnString
    },
    showTactic(tactic: Tactic) {
      const thisTacticIntID = tactic.originalTacticID ? tactic.originalTacticID.intID : tactic.id.intID
      this.$router.push({ path: 'tactic/' + thisTacticIntID })
    },
    hasTacticSummary(tactic: Tactic): boolean {
      // Summary content lives in the tacticSummary S3 object (fetched lazily), not in the legacy summary string.
      return !!(tactic.tacticSummary?.contentUrl || (tactic.summary && tactic.summary !== '<p></p>'))
    },
    showSummaryOverlay(event: Event, tactic: Tactic) {
      const contentUrl = tactic.tacticSummary?.contentUrl
      // Use already-loaded content if present, otherwise fetch it from S3 and cache it on the tactic.
      if (tactic.summary && tactic.summary !== '<p></p>') {
        this.currentOverlayHTML = tactic.summary
      } else if (contentUrl) {
        this.currentOverlayHTML = ''
        this.$store.getters.services.tactics
          .fetchSummaryContent(contentUrl)
          .then((html: string) => {
            tactic.summary = html
            this.currentOverlayHTML = html
          })
      } else {
        this.currentOverlayHTML = ''
      }
      // @ts-expect-error - OverlayPanel ref exposes show()
      this.$refs.richTextFieldOverlay.show(event)
    },
    hideSummaryOverlay() {
      // @ts-expect-error - OverlayPanel ref exposes hide()
      this.$refs.richTextFieldOverlay.hide()
    },
    onAddTactic() {
      // TODO: show error message if user is viewer
      if (this.canUserAddTactics) {
        this.$router.push({
          path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$store.getters.currentPlan.id.intID}/tactic/0`
        })
      } else {
        Vue.prototype.$toast.add({
          severity: AlertMessageSeverity.warn,
          summary: 'Viewers do not have the ability to add to or edit the Plan.',
          life: 3000,
        })
      }
    },
    mapImageFieldUrls() {
      this.customImageFieldUrlsMap = []
      this.currentTactics.forEach((tactic) => {
        tactic.typeValues.forEach((typeValue) => {
          const typeField = this.universalCustomFieldsWithData.find(
            (field) => field.id.intID == typeValue.tacticFieldId.intID
          )
          if (typeField?.type == 'image' && typeValue.value) {
            this.$store.getters.services.tactics
              .getMediaAsset(typeValue.value)
              .then((response: MediaAsset) => {
                this.customImageFieldUrlsMap =
                  this.customImageFieldUrlsMap.concat([
                    {
                      tacticId: tactic.id.intID,
                      fieldId: typeValue.tacticFieldId.intID,
                      url: response.fileLocation,
                    },
                  ])
              })
          }
        })
      })
    },
    getCustomImageFieldUrl(tacticId, fieldId) {
      const val = this.customImageFieldUrlsMap.find((imageField) => {
        if (
          imageField.fieldId == fieldId.intID &&
          imageField.tacticId == tacticId.intID
        ) {
          return true
        }
        return false
      })
      return val ? val.url : ''
    },
    getTitleForTactic(tactic: Tactic) {
      let returnString = tactic.title
      if (this.isLeadPlan && tactic.abbreviatedPlanName !== '') {
        if (returnString.length > 0) {
          returnString = tactic.abbreviatedPlanName + '  |  ' + returnString
        } else {
          returnString = tactic.abbreviatedPlanName
        }
      }
      return returnString
    },
    getRepeatsDisplayForTactic(tactic: Tactic): string {
      // TODO: Copied from src/views/TacticSummary.vue, consider combining in to src/AppStore.ts
      // Strip any EXDATE tail (added when breaking an instance out of this series) before
      // comparing — getRepeatOptionsForTactic's values never include one, so a tactic broken off
      // from would otherwise never match a preset and fall back to the unhelpful 'Custom' label.
      const rruleWithoutExDates = stripExDates(tactic.rrule || '')
      const endDatePosition = rruleWithoutExDates.indexOf(';UNTIL')
      const rruleWithoutEndDate = endDatePosition > -1 ? rruleWithoutExDates.substring(0, endDatePosition) : rruleWithoutExDates
      const thisValue = this.getRepeatOptionsForTactic(tactic).find((option) => option['value'] == rruleWithoutEndDate)
      return thisValue ? thisValue['label'] : 'Custom'
    },
    getRepeatOptionsForTactic(tactic: Tactic): object[] {
      // TODO: Copied from src/views/TacticDetail.vue, consider combining in to src/AppStore.ts
      const startDate = new Date(tactic.startDate)
      const year = startDate.getFullYear()
      const month =
        startDate.getMonth() > 8
          ? startDate.getMonth() + 1
          : '0' + (startDate.getMonth() + 1)
      const date =
        startDate.getDate() > 9
          ? startDate.getDate()
          : '0' + startDate.getDate()
      const hours =
        startDate.getHours() > 9
          ? startDate.getHours()
          : '0' + startDate.getHours()
      const minutes =
        startDate.getMinutes() > 9
          ? startDate.getMinutes()
          : '0' + startDate.getMinutes()
      const seconds =
        startDate.getSeconds() > 9
          ? startDate.getSeconds()
          : '0' + startDate.getSeconds()

      const dtStart = tactic.startDate
        ? `DTSTART=${year}${month}${date}T${hours}${minutes}${seconds};`
        : ''
      return [
        {
          value: `${dtStart}FREQ=DAILY`,
          label: 'Daily',
        },
        {
          value: `${dtStart}FREQ=WEEKLY;WKST=SU;BYDAY=${format(
            startDate,
            'EEEEEE'
          ).toUpperCase()}`,
          label: `Weekly on ${format(startDate, 'EEEE')}`,
        },
        {
          value: `${dtStart}FREQ=MONTHLY;BYMONTHDAY=${format(
            startDate,
            'd'
          )}`,
          label: `Monthly on the ${format(startDate, 'do')}`,
        },
        {
          ...this.getMonthlyByDay(startDate, dtStart),
        },
        {
          value: `${dtStart}FREQ=YEARLY`,
          label: `Annually on ${format(startDate, 'MMM')} ${format(
            startDate,
            'd'
          )}`,
        },
        {
          value: 'custom',
          label: 'Custom',
        },
      ]
    },
    getMonthlyByDay(
      startDate: Date,
      dtStart: string,
      onlyReturnMonthlyRule = false
    ) {
      // TODO: Copied from src/views/TacticDetail.vue, consider combining in to src/AppStore.ts
      const d = new Date(startDate.getTime())
      const dayOfWeek = d.getDay()
      const month = d.getMonth()
      const datesWithSameDayOfWeek = [] as Date[]

      // Get the first instance of the day of week in the month
      d.setDate(1)
      while (d.getDay() !== dayOfWeek) {
        d.setDate(d.getDate() + 1)
      }

      // get all instances of day of week
      while (d.getMonth() === month) {
        datesWithSameDayOfWeek.push(new Date(d))
        d.setDate(d.getDate() + 7)
      }

      // find our start date's index of those
      let indexOfStartDate = 0
      datesWithSameDayOfWeek.forEach((date, index) => {
        if (isSameDay(startDate, date)) {
          indexOfStartDate = index
        }
      })

      const isFifthInstanceOfWeekDay = indexOfStartDate > 4

      if (onlyReturnMonthlyRule) {
        return {
          value: isFifthInstanceOfWeekDay
            ? `BYSETPOS=-1;BYDAY=${format(startDate, 'EEEEEE').toUpperCase()}`
            : `BYSETPOS=${indexOfStartDate + 1};BYDAY=${format(
              startDate,
              'EEEEEE'
            ).toUpperCase()}`,
          label: isFifthInstanceOfWeekDay
            ? `Monthly on last ${format(startDate, 'eeee')} of the month`
            : `The ${indexOfStartDate + 1}${['st', 'nd', 'rd'][
            ((((indexOfStartDate + 1 + 90) % 100) - 10) % 10) - 1
            ] || 'th'
            } ${format(startDate, 'eeee')} of the month`,
        }
      }
      return {
        value: isFifthInstanceOfWeekDay
          ? `${dtStart}FREQ=MONTHLY;BYSETPOS=-1;BYDAY=${format(
            startDate,
            'EEEEEE'
          ).toUpperCase()}`
          : `${dtStart}FREQ=MONTHLY;BYSETPOS=${indexOfStartDate + 1
          };BYDAY=${format(startDate, 'EEEEEE').toUpperCase()}`,
        label: isFifthInstanceOfWeekDay
          ? `Monthly on last ${format(startDate, 'eeee')} of the month`
          : `Monthly on the ${indexOfStartDate + 1}${['st', 'nd', 'rd'][
          ((((indexOfStartDate + 1 + 90) % 100) - 10) % 10) - 1
          ] || 'th'
          } ${format(startDate, 'eeee')} of the month`,
      }
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.channels-view {
  .tag-title {
    display: block;
  }

  .export-message {
    font-size: 1rem;
  }

  .empty-message {
    height: 100%;
  }

  .p-datatable-thead>tr>th,
  .p-datatable-tbody>tr>td {
    max-width: 30rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .p-buttonset {
    .p-button {
      border-color: #efefef;
      background-color: #efefef;
      color: #333333;
      white-space: nowrap;

      &:disabled {
        border-color: #000000;
        background-color: #000000;
        color: #ffffff;
        opacity: 1;
      }
    }
  }

  .type-field-container {
    // max-width: calc(100% - 15rem);
  }

  .platform-field-container {
    max-width: calc(100% - 15rem);
  }

  .custom-field-image-budgets-view {
    border: 0;
    padding: 0;
    cursor: pointer;

    img {
      height: 50px;
    }
  }

  .p-datatable-wrapper span.is-lead {
    display: flex;
    column-gap: 1rem;

    &::before {
      content: '\e925'; // pi-home
      display: block;
      font-family: 'primeicons';
      font-size: 1.6rem;
      speak: none;
      font-style: normal;
      font-weight: normal;
      font-variant: normal;
      text-transform: none;
    }
  }

  .p-sortable-column {
    .p-sortable-column-icon {
      opacity: 0;
    }

    &:hover,
    &:focus {
      .p-sortable-column-icon {
        opacity: 1;
      }
    }
  }

  .parking-lot-switch-container {
    .p-inputswitch {
      .p-inputswitch-slider {
        background: green;

        &:hover {
          background: green;
        }
      }

      &.p-inputswitch-checked {
        .p-inputswitch-slider {
          background: #666;

          &:hover {
            background: #666;
          }
        }
      }
    }
  }
}
</style>
